import React, { useCallback, useEffect, useState } from "react";
import mesApi from "../../../Api/mes.api";
import {
  PhaseMaterialConsumptionRow,
  PhaseMaterialDemandRow,
  CandidateLot,
} from "../../../types/mes";
import { toast } from "react-toastify";
import { t } from "../../../i18n/locale";

interface Props {
  batchId: number;
  /** Kodovi faza koje ovaj tab pokriva (npr. ['packaging']). */
  phaseCodes: string[];
}

/**
 * Ekran izvršenja: FIFO daje PRIJEDLOG lotova za fazne materijale, a operater ovdje
 * na kraju faze potvrđuje ili zamijeni lot. Zaliha se skida tek na „Potvrdi utrošak".
 */
export default function PhaseMaterialConfirm({ batchId, phaseCodes }: Props) {
  const [rows, setRows] = useState<PhaseMaterialConsumptionRow[]>([]);
  const [demands, setDemands] = useState<PhaseMaterialDemandRow[]>([]);
  const [lotOptions, setLotOptions] = useState<Record<number, CandidateLot[]>>({});
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [allRows, allDemands] = await Promise.all([
        mesApi.getBatchPhaseMaterialConsumption(batchId),
        mesApi.getBatchPhaseMaterialDemands(batchId),
      ]);
      setRows(allRows.filter((r) => r.phaseTypeCode != null && phaseCodes.includes(r.phaseTypeCode)));
      setDemands(allDemands.filter((d) => d.phaseTypeCode != null && phaseCodes.includes(d.phaseTypeCode)));
    } catch (e: any) {
      toast.error(e?.message || t("Greška pri dohvatu faznih materijala"));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId, phaseCodes.join("|")]);

  useEffect(() => {
    load();
  }, [load]);

  const ensureLots = async (aliasId: number | null) => {
    if (aliasId == null || lotOptions[aliasId]) return;
    try {
      const lots = await mesApi.getCandidateLots(aliasId);
      setLotOptions((prev) => ({ ...prev, [aliasId]: lots }));
    } catch {
      /* tiho — dropdown ostaje na trenutnom lotu */
    }
  };

  const swap = async (row: PhaseMaterialConsumptionRow, lotId: number) => {
    if (!lotId || lotId === row.materialLotId) return;
    setBusy(true);
    try {
      await mesApi.swapPhaseMaterialLot(row.ID, lotId);
      await load();
    } catch (e: any) {
      toast.error(e?.message || "Zamjena lota nije uspjela");
    } finally {
      setBusy(false);
    }
  };

  const propose = async () => {
    setBusy(true);
    try {
      await mesApi.proposePhaseMaterials(batchId);
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("Prijedlog nije uspio"));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    try {
      const r = await mesApi.confirmPhaseMaterials(batchId, { phaseCodes });
      toast.success(t("Potvrđeno stavki: {count}", { count: r.confirmed }));
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("Potvrda nije uspjela"));
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="tablet-wo-pm tablet-wo-pm-empty">{t("Učitavanje materijala…")}</div>;
  }

  // Nema definiranog normativa za ovu fazu.
  if (rows.length === 0 && demands.length === 0) {
    return (
      <div className="tablet-wo-pm tablet-wo-pm-empty">
        <span>{t("Nema definiranih materijala za ovu fazu.")}</span>
      </div>
    );
  }

  // Ima normativa, ali FIFO nije ništa predložio — najčešće nema zalihe na skladištu.
  if (rows.length === 0) {
    return (
      <div className="tablet-wo-pm">
        <div className="tablet-wo-pm-head">
          <span className="tablet-wo-pm-title">{t("Dodatni materijali (nema zalihe)")}</span>
        </div>
        <div className="mes-table-wrap">
          <table className="tablet-wo-pm-table">
          <thead>
            <tr>
              <th>{t("Materijal")}</th>
              <th>{t("Potrebno")}</th>
              <th>{t("Status")}</th>
            </tr>
          </thead>
          <tbody>
            {demands.map((d) => (
              <tr key={d.phaseMaterialId}>
                <td>{d.materialName}</td>
                <td>{d.requiredQty.toFixed(3)}</td>
                <td>
                  <span className="tablet-wo-pm-badge warn">{t("Nema zalihe")}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        <div className="tablet-wo-pm-actions">
          <button type="button" className="tablet-wo-btn" disabled={busy} onClick={propose}>
            {t("Predloži (FIFO)")}
          </button>
        </div>
      </div>
    );
  }

  const anyUnconfirmed = rows.some((r) => !r.confirmed);
  const totalCost = rows.reduce((s, r) => s + (r.cost || 0), 0);

  return (
    <div className="tablet-wo-pm">
      <div className="tablet-wo-pm-head">
        <span className="tablet-wo-pm-title">{t("Dodatni materijali (prijedlog FIFO)")}</span>
        <span className="tablet-wo-pm-total">{t("Trošak: {amount}", { amount: totalCost.toFixed(2) })}</span>
      </div>
      <div className="mes-table-wrap">
        <table className="tablet-wo-pm-table">
        <thead>
          <tr>
            <th>{t("Materijal")}</th>
            <th>{t("Količina")}</th>
            <th>{t("Lot")}</th>
            <th>{t("Status")}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const opts = r.ingredientAliasId != null ? lotOptions[r.ingredientAliasId] : undefined;
            return (
              <tr key={r.ID} className={r.confirmed ? "is-confirmed" : ""}>
                <td>{r.material}</td>
                <td>{r.qty.toFixed(3)}</td>
                <td>
                  {r.confirmed ? (
                    <span>{r.lotNumber || `Lot #${r.materialLotId}`}</span>
                  ) : (
                    <select
                      className="tablet-wo-pm-select"
                      value={r.materialLotId ?? ""}
                      disabled={busy}
                      onFocus={() => ensureLots(r.ingredientAliasId)}
                      onChange={(e) => swap(r, parseInt(e.target.value))}
                    >
                      {/* trenutni lot uvijek prisutan kao opcija */}
                      {r.materialLotId != null &&
                        !(opts || []).some((o) => o.materialLotId === r.materialLotId) && (
                          <option value={r.materialLotId}>
                            {r.lotNumber || `Lot #${r.materialLotId}`}
                          </option>
                        )}
                      {(opts || []).map((o) => (
                        <option key={o.materialLotId} value={o.materialLotId}>
                          {(o.lotNumber || `Lot #${o.materialLotId}`) +
                            t(" — stanje {remaining}", { remaining: o.remaining.toFixed(2) })}
                        </option>
                      ))}
                    </select>
                  )}
                </td>
                <td>
                  {r.confirmed ? (
                    <span className="tablet-wo-pm-badge ok">{t("Potvrđeno")}</span>
                  ) : (
                    <span className="tablet-wo-pm-badge">{t("Prijedlog")}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      </div>
      {anyUnconfirmed && (
        <div className="tablet-wo-pm-actions">
          <button
            type="button"
            className="tablet-wo-btn primary"
            disabled={busy}
            onClick={confirm}
          >
            {busy ? t("Spremanje…") : t("Potvrdi utrošak")}
          </button>
        </div>
      )}
    </div>
  );
}
